import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { t } from '../i18n/i18n'
import {
   ActionPlan,
   ActionPlanStatus,
   ConsequenceScale,
   Cost,
   CostCategory,
   DemandAnalysis,
   DemandAnalysisQuestionStatus,
   DemandArea,
   DemandSubType,
   DemandType,
   FinancialYear,
   ProbabilityScale,
   RiskAnalysis,
   RiskLevel,
   RiskStatus,
   SWOTType,
} from '../api/schemas/schema'
import { findQuestionParentToActionPlan } from './actionPlanHelper'
import { actionPlanStatusString, translateEnumValue } from './enumHelper'
import { formatDate, formatToSwedishCrowns } from './stringHelper'
import { findSWOTParentToActionPlan } from './swotHelper'
import { getFullName } from './accountHelper'
import { findConsequenceRiskParentToActionPlan, findSimplifiedRiskParentToActionPlan } from './riskAnalysisHelper'
import { saveAs } from 'file-saver'

const stripHtmlTags = (input: string) => {
   const div = document.createElement('div')
   div.innerHTML = input
   return div.innerText || div.textContent || ''
}

export const exportToExcel = (rows: any[], sheetName: string, fileName: string) => {
   const workbook = XLSX.utils.book_new()
   const ws1 = XLSX.utils.json_to_sheet(rows)
   XLSX.utils.book_append_sheet(workbook, ws1, sheetName)
   XLSX.writeFile(workbook, `${fileName}_${formatDate(new Date())}.xlsx`)
}

export const exportDemandActionPlansToExcel = (analysis: DemandAnalysis, filteredActionPlans: ActionPlan[]) => {
   const { questions, name } = analysis
   const rows: any[] = []

   filteredActionPlans.forEach((actionPlan) => {
      const question = findQuestionParentToActionPlan(questions, actionPlan)
      const budgetedCost = actionPlan.budgetedCost

      rows.push({
         Nr: actionPlan.index,
         Fråga: stripHtmlTags(question.text),
         'Förslag till åtgärd': question.proposedAction ?? '',
         'Beslut om åtgärd': actionPlan.actionNeeded ? actionPlan.decidedAction : 'Inga åtgärder ska vidtas',
         Beslutskommentar: actionPlan.decidedActionComment ?? '',
         Ansvarig: actionPlan.responsible ? getFullName(actionPlan.responsible) : '',
         Prioritet: actionPlan.priority ?? '',
         Efterlevnadsnivå: question.answer.text,
         'Klart senast': actionPlan.deadline ? formatDate(actionPlan.deadline) : '',
         'Uppskattad kostnad': question.estimatedCost || question.estimatedCost === 0 ? `${formatToSwedishCrowns(question.estimatedCost)} kr` : '',
         'Budgeterad kostnad': budgetedCost || budgetedCost === 0 ? `${formatToSwedishCrowns(budgetedCost)} kr ` : '',
         Process: question.processes[0],
         Beslutsstatus: actionPlanStatusString(actionPlan.status),
      })
   })
   exportToExcel(rows, t('ActionPlan'), `${t('ActionPlan')}_${name}`)
}

export const exportSWOTActionPlansToExcel = (analysis: RiskAnalysis, actionPlans: ActionPlan[]) => {
   const { swoTs, name } = analysis
   const rows: any[] = []

   actionPlans.forEach((actionPlan) => {
      const swot = findSWOTParentToActionPlan(swoTs, actionPlan)
      const budgetedCost = actionPlan.budgetedCost

      rows.push({
         Nr: actionPlan.index,
         'SWOT Typ': translateEnumValue(SWOTType, swot.type),
         Rubrik: swot.title,
         Beskrivning: swot.description,
         'Förslag till åtgärd': swot.proposedAction ?? '',
         'Beslut om åtgärd': actionPlan.actionNeeded ? actionPlan.decidedAction : 'Inga åtgärder ska vidtas',
         Beslutskommentar: actionPlan.decidedActionComment ?? '',
         Ansvarig: actionPlan.responsible ? getFullName(actionPlan.responsible) : '',
         Prioritet: actionPlan.priority ?? '',
         'Klart senast': actionPlan.deadline ? formatDate(actionPlan.deadline) : '',
         'Uppskattad kostnad': swot.estimatedCost || swot.estimatedCost === 0 ? `${formatToSwedishCrowns(swot.estimatedCost)} kr ` : '',
         'Budgeterad kostnad': budgetedCost || budgetedCost === 0 ? `${formatToSwedishCrowns(budgetedCost)} kr ` : '',
         Beslutsstatus: actionPlanStatusString(actionPlan.status),
      })
   })
   exportToExcel(rows, t('RiskManagementPlan'), `${t('RiskManagementPlan')}_${name}`)
}

export const exportSimplifiedRiskActionPlansToExcel = (analysis: RiskAnalysis, actionPlans: ActionPlan[]) => {
   const { simplifiedRisks, name } = analysis
   const rows: any[] = []

   actionPlans.forEach((actionPlan) => {
      const risk = findSimplifiedRiskParentToActionPlan(simplifiedRisks, actionPlan)
      const budgetedCost = actionPlan.budgetedCost

      rows.push({
         Nr: actionPlan.index,
         Risk: risk.scenario,
         Konsekvens: risk.consequence,
         Källa: risk.source,
         'Sårbarheter eller nuvarande skydd': risk.currentState,
         'Underliggande motivering och ställningstagande': risk.comment,
         'Riskbedömning:': risk.status ? translateEnumValue(RiskStatus, risk.status) : '',
         'Förslag till åtgärd': risk.proposedAction ?? '',
         'Beslut om åtgärd': actionPlan.actionNeeded ? actionPlan.decidedAction : 'Inga åtgärder ska vidtas',
         Beslutskommentar: actionPlan.decidedActionComment ?? '',
         Ansvarig: actionPlan.responsible ? getFullName(actionPlan.responsible) : '',
         Prioritet: actionPlan.priority ?? '',
         'Klart senast': actionPlan.deadline ? formatDate(actionPlan.deadline) : '',
         'Uppskattad kostnad': risk.estimatedCost || risk.estimatedCost === 0 ? `${formatToSwedishCrowns(risk.estimatedCost)} kr` : '',
         'Budgeterad kostnad': budgetedCost || budgetedCost === 0 ? `${formatToSwedishCrowns(budgetedCost)} kr ` : '',
         Beslutsstatus: actionPlanStatusString(actionPlan.status),
      })
   })
   exportToExcel(rows, t('RiskManagementPlan'), `${t('RiskManagementPlan')}_${name}`)
}
export const exportConsequenceAndProbabilityRiskActionPlansToExcel = (analysis: RiskAnalysis, actionPlans: ActionPlan[]) => {
   const { consequenceAndProbabilityRisks, name } = analysis
   const rows: any[] = []

   actionPlans.forEach((actionPlan) => {
      const risk = findConsequenceRiskParentToActionPlan(consequenceAndProbabilityRisks, actionPlan)
      const budgetedCost = actionPlan.budgetedCost

      rows.push({
         Nr: actionPlan.index,
         Risk: risk.scenario,
         Konsekvens: risk.consequence,
         Källa: risk.source,
         'Sårbarheter eller nuvarande skydd': risk.currentState,
         'Underliggande motivering och ställningstagande': risk.comment,
         'Risknivå:': translateEnumValue(RiskLevel, risk.riskLevel),
         'Konsekvens liv och hälsa:': risk.consequenceLifeHealth ? translateEnumValue(ConsequenceScale, risk.consequenceLifeHealth) : '',
         'Konsekvens ekonomi:': risk.consequenceEconomy ? translateEnumValue(ConsequenceScale, risk.consequenceEconomy) : '',
         'Konsekvens miljö:': risk.consequenceEnviroment ? translateEnumValue(ConsequenceScale, risk.consequenceEnviroment) : '',
         'Konsekvens varumärke:': risk.consequenceBrand ? translateEnumValue(ConsequenceScale, risk.consequenceBrand) : '',
         'Sannolikhet:': risk.probability ? translateEnumValue(ProbabilityScale, risk.probability) : '',
         'Förslag till åtgärd': risk.proposedAction ?? '',
         'Beslut om åtgärd': actionPlan.actionNeeded ? actionPlan.decidedAction : 'Inga åtgärder ska vidtas',
         Beslutskommentar: actionPlan.decidedActionComment ?? '',
         Ansvarig: actionPlan.responsible ? getFullName(actionPlan.responsible) : '',
         Prioritet: actionPlan.priority ?? '',
         'Klart senast': actionPlan.deadline ? formatDate(actionPlan.deadline) : '',
         'Uppskattad kostnad': risk.estimatedCost || risk.estimatedCost === 0 ? `${formatToSwedishCrowns(risk.estimatedCost)} kr` : '',
         'Budgeterad kostnad': budgetedCost || budgetedCost === 0 ? `${formatToSwedishCrowns(budgetedCost)} kr ` : '',
         Beslutsstatus: actionPlanStatusString(actionPlan.status),
      })
   })
   exportToExcel(rows, t('RiskManagementPlan'), `${t('RiskManagementPlan')}_${name}`)
}

export const exportCostsToExcel = (financialYear: FinancialYear) => {
   const { costs } = financialYear
   const rows: any[] = []

   costs.forEach((cost) => {
      rows.push({
         Räkenskapsår: financialYear.name,
         Kategori: translateEnumValue(CostCategory, cost.post.costCategory),
         Post: cost.post.name,
         Beskrivning: cost.description,
         Datum: formatDate(cost.created),
         Budget: formatToSwedishCrowns(cost.budget ?? 0) + ' kr',
         Utfall: formatToSwedishCrowns(cost.result ?? 0) + ' kr',
      })
   })
   exportToExcel(rows, t('Costs'), `${t('FinancialYear')}_${financialYear.name}`)
}

export const exportFinancialYearActionPlans = (filteredCosts: Cost[], actionsPlans: ActionPlan[], financialYearName: string) => {
   const rows: any[] = []

   filteredCosts.forEach((cost) => {
      const actionPlan = actionsPlans.find((a) => a.costId === cost.id)
      const decidedAction = !actionPlan ? '' : actionPlan.actionNeeded ? actionPlan.decidedAction : 'Inga åtgärder ska vidtas'
      const budgetedCost = actionPlan.budgetedCost

      rows.push({
         Nr: actionPlan ? actionPlan.index : '',
         Kategori: translateEnumValue(CostCategory, cost.post.costCategory),
         Post: cost.post.name ?? translateEnumValue(CostCategory, cost.post.costCategory),
         Budget: formatToSwedishCrowns(cost.budget ?? 0) + ' kr',
         Utfall: formatToSwedishCrowns(cost.result ?? 0) + ' kr',
         'Förslag till åtgärd': cost.proposedAction ?? '',
         Förslagskommentar: cost.comment,
         'Uppskattad kostnad för åtgärd': cost.estimatedCost || cost.estimatedCost === 0 ? formatToSwedishCrowns(cost.estimatedCost) + ' kr' : null,
         'Budgeterad kostnad': budgetedCost || budgetedCost === 0 ? `${formatToSwedishCrowns(budgetedCost)} kr ` : '',
         'Förslag av': getFullName(cost.proposedBy),
         'Beslut om åtgärd': decidedAction,
         Beslutskommentar: actionPlan && actionPlan.decidedActionComment ? actionPlan.decidedActionComment : '',
         Ansvarig: actionPlan && actionPlan.responsible ? getFullName(actionPlan.responsible) : '',
         Prioritet: actionPlan && actionPlan.priority ? actionPlan.priority : '',
         'Klart senast': actionPlan && actionPlan.deadline ? formatDate(actionPlan.deadline) : '',
         Beslutsstatus: actionPlanStatusString(actionPlan ? actionPlan.status : ActionPlanStatus.NotDecided),
      })
   })
   exportToExcel(rows, t('ActionPlan'), `${financialYearName}_${t('ActionPlan')}`)
}

export const exportLawListToExcel = async (analysis: DemandAnalysis) => {
   const { questions, name } = analysis

   const filteredQuestions = questions.filter(
      (question) =>
         question.status !== DemandAnalysisQuestionStatus.NotAnswered &&
         question.answer.text !== 'Inte relevant' &&
         question.demandType === DemandType.PublicAuthorityDemand &&
         (question.demandSubType === DemandSubType.Law || question.demandSubType === DemandSubType.EUDirective)
   )

   const workbook = new ExcelJS.Workbook()
   const worksheet = workbook.addWorksheet(t('Lawlist'))

   worksheet.pageSetup.orientation = 'landscape'

   for (let i = 0; i < 9; i++) {
      worksheet.addRow([])
   }

   const imageBuffer = await fetch(`${process.env.REACT_APP_BASE_URL}/365Robust.png`).then((res) => res.arrayBuffer())

   const imageId = (workbook as any).addImage({
      buffer: imageBuffer,
      extension: 'png',
   })

   worksheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 300, height: 180 },
   })

   worksheet.mergeCells('A1:E9')
   worksheet.addRow([])

   worksheet.mergeCells('A11:B11')
   worksheet.getCell('A11').value = t('Source365Robust')
   worksheet.getCell('A11').font = { bold: true, size: 14 }
   worksheet.getCell('A11').alignment = { vertical: 'middle', horizontal: 'left' }

   worksheet.mergeCells('C11:E11')
   worksheet.getCell('C11').value = `Kravbedömning: ${name}`
   worksheet.getCell('C11').font = { bold: true, size: 12 }
   worksheet.getCell('C11').alignment = { vertical: 'middle', horizontal: 'left' }

   worksheet.mergeCells('A12:E12')
   worksheet.getCell('A12').value = `${t('PrintDate')}:  ${formatDate(new Date(), true)} `
   worksheet.getCell('A12').alignment = { vertical: 'middle', horizontal: 'left' }

   worksheet.addRow([])

   worksheet.addRow(['Kravkälla', 'Process', 'Typ av krav', 'Utgåva', 'Webblänk'])
   worksheet.getRow(15).font = { bold: true }

   const sortedQuestions = [...filteredQuestions].sort((a, b) => (a.demandSource ?? '').localeCompare(b.demandSource ?? ''))

   sortedQuestions.forEach((question) => {
      if (!question) return

      worksheet.addRow([
         question.demandSource ?? '',
         translateEnumValue(DemandArea, question.demandArea) ?? '',
         translateEnumValue(DemandSubType, question.demandSubType) ?? '',
         question.demandVersion ?? '',
         question.demandUrl ?? '',
      ])
   })

   worksheet.addRow([])

   worksheet.columns.forEach((column) => {
      let maxLength = 10
      column.eachCell({ includeEmpty: true }, (cell) => {
         if (cell.value) {
            const val = cell.value.toString() ?? ''
            maxLength = Math.max(maxLength, val.length)
            column.width = maxLength + 2
         }
      })
   })

   const buffer = await workbook.xlsx.writeBuffer()
   const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

   saveAs(blob, `${t('LawRequirements')}_${name}_${formatDate(new Date())}.xlsx`)
}
